// Editable control points are [insetMm, zMm, radiusMm?]. Radius zero is sharp.
// Only the renderer samples circles; saved designs keep the actual radius.
export const RADIUS_SIDE_PROFILE_MM = [
  [0, -52.5, 2], [2.05, -11.5, 2], [16.412, -11.5, 0],
  [16.412, -8, 0], [10.912, -8, 0], [10.912, 7.5, 2]
];

const distance = (a, b) => Math.hypot(a[0] - b[0], a[1] - b[1]);

export function sampleProfile(points, { closed = false } = {}) {
  if (!Array.isArray(points) || points.length < (closed ? 3 : 2) || points.length > 200 ||
      points.some((p, i) => !Array.isArray(p) || p.length < 2 || p.length > 3 ||
        !p.every(Number.isFinite) || (p[2] || 0) < 0 ||
        (i > 0 && distance(p, points[i - 1]) < 1e-8))) {
    throw new Error('Profile requires 2–200 points as [inset, Z, radius].');
  }
  if (closed && distance(points[0], points[points.length - 1]) < 1e-8) {
    throw new Error('Do not repeat the first point at the end of a closed profile.');
  }
  const corners = points.map((b, i) => {
    const radius = b[2] || 0;
    if (!radius) return { tangent: 0, samples: [b.slice(0, 2)] };
    // The end points meet the front/back face, extending toward the leaf centre.
    const a = points[i - 1] || (closed ? points[points.length - 1] : [b[0] + 10000, b[1]]);
    const c = points[i + 1] || (closed ? points[0] : [b[0] + 10000, b[1]]);
    const la = distance(a, b), lc = distance(c, b);
    const u = [(a[0] - b[0]) / la, (a[1] - b[1]) / la];
    const v = [(c[0] - b[0]) / lc, (c[1] - b[1]) / lc];
    const angle = Math.acos(Math.max(-1, Math.min(1, u[0] * v[0] + u[1] * v[1])));
    if (!Number.isFinite(angle) || angle < 1e-6 || Math.PI - angle < 1e-6) {
      throw new Error(`Profile point ${i + 1}: radius requires two edges with different directions.`);
    }
    const tangent = radius / Math.tan(angle / 2);
    const bisector = Math.hypot(u[0] + v[0], u[1] + v[1]);
    const length = radius / Math.sin(angle / 2);
    const center = [b[0] + (u[0] + v[0]) / bisector * length,
      b[1] + (u[1] + v[1]) / bisector * length];
    const first = [b[0] + u[0] * tangent, b[1] + u[1] * tangent];
    const last = [b[0] + v[0] * tangent, b[1] + v[1] * tangent];
    const start = Math.atan2(first[1] - center[1], first[0] - center[0]);
    let sweep = Math.atan2(last[1] - center[1], last[0] - center[0]) - start;
    while (sweep > Math.PI) sweep -= 2 * Math.PI;
    while (sweep < -Math.PI) sweep += 2 * Math.PI;
    const segments = Math.min(128, Math.max(2, Math.ceil(Math.abs(sweep) /
      Math.min(Math.PI / 18, 2 * Math.acos(Math.max(-1, 1 - 0.01 / radius))))));
    const samples = Array.from({ length: segments + 1 }, (_, n) => n === 0 ? first : n === segments ? last : [
      center[0] + radius * Math.cos(start + sweep * n / segments),
      center[1] + radius * Math.sin(start + sweep * n / segments)
    ]);
    return { tangent, samples };
  });
  for (let i = 1; i < points.length; i++) {
    if (corners[i - 1].tangent + corners[i].tangent > distance(points[i - 1], points[i]) + 1e-7) {
      throw new Error(`Profile points ${i}–${i + 1}: radii do not fit on the shared edge. Reduce R.`);
    }
  }
  if (closed && corners[0].tangent + corners[corners.length - 1].tangent > distance(points[0], points[points.length - 1]) + 1e-7) {
    throw new Error('The last and first point radii do not fit on the shared edge.');
  }
  return corners.flatMap((c) => c.samples).map(([x, z]) => [
    Math.abs(x) < 1e-9 ? 0 : x, Math.round(z * 1e10) / 1e10
  ]).filter((p, i, all) => i === 0 || distance(p, all[i - 1]) > 1e-8);
}
